import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { crearResultado, getProyectosByUser } from "../controllers/operaciones";
import msgLoadingData from "../data/msgLoadingData.json";
import msgLoadingUnidadData from "../data/msgLoadingUnidadData.json";
import { iDataStateJobs, TipoEstructura } from "../interfaces/iDataStateJobs";
import { iProyectoABP, iProyectoUA } from "../interfaces/Proyecto/iProyecto";
import { iJobContextProps } from "../interfaces/UmmiaApps/iJobContext";
import { socket } from "../services/sockets";
import { useAuthContext } from "./Auth";
import { subscribeToEvents, unsubscribeFromEvents } from "./socketHandler";

const JobContext = createContext<iJobContextProps>({} as iJobContextProps);

const JobProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { user, updateCreditInfo } = useAuthContext();

  const initialState: iDataStateJobs = {
    wsProject: {},
    showNotification: false,
    isCompleted: false,
    isFailed: false,
    isProcessing: false,
    isMisProyectosLoading: true,
    etapa: 0,
    jobs: {
      id: "",
      status: "",
      title: "",
      etapa: 0,
      tipoEstructura: "",
    },
    propuestaUsuario: {},
    proyectoId: null,
    resultadoId: null,
    misProyectos: [],
    misProyectosIncompletos: null,
    userId: user?.id || "",
    idOrganizacion: user?.id_organizacion || "",
    msgLoading: "",
    msgLoadingUnidad: "",
    socketEventPrefix: "",
    tipoEstructura: "unidad_activa",
    isProjectFinished: false,
    in_tokens: 0,
    out_tokens: 0,
    costo: 0,
  };

  const [stateJob, setStateJob] = useState<iDataStateJobs>(initialState);

  const {
    userId,
    etapa,
    proyectoId,
    isCompleted,
    isFailed,
    jobs,
    wsProject,
    socketEventPrefix,
  } = stateJob;

  const isSubscribed = useRef(false);

  useEffect(() => {
    //Aqui se instancia el userId que proviene del contexto de Auth
    if (user && user.id) {
      setStateJob((prevState) => ({
        ...prevState,
        userId: user.id,
        idOrganizacion: user.id_organizacion,
      }));
    }
  }, [user]);

  //Se ejecuta cuando detecta que el proyecto llego a su etapa final
  useEffect(() => {
    if (stateJob.isProjectFinished) {
      //loadUserProjects();
      setStateJob((prevState) => ({ ...prevState, isProjectFinished: false }));
    }
  }, [stateJob.isProjectFinished]);

  const loadLocalProjects = useCallback(() => {
    const localStorageJobs = localStorage.getItem("jobs");
    if (localStorageJobs) {
      const jobsPendientes = JSON.parse(localStorageJobs);
      setStateJob((prevState) => ({ ...prevState, jobs: jobsPendientes }));
    }
  }, []);

  useEffect(() => {
    console.log(stateJob);
  }, [stateJob]);

  //Una vez instanciado el usuario carga proyectos locales y remotos
  useEffect(() => {
    if (userId) {
      loadLocalProjects();
    }
  }, [userId, loadLocalProjects]);

  useEffect(() => {
    if (stateJob.misProyectos) {
      setStateJob((prevState) => ({
        ...prevState,
        isMisProyectosLoading: false,
      }));
    }
  }, [stateJob.misProyectos]);

  useEffect(() => {
    if (!isSubscribed.current) {
      const eventPrefix = `${socketEventPrefix}_etapa_${etapa}`;
      console.log("useEffect eventPrefix", eventPrefix);
      const subscription = subscribeToEvents(
        eventPrefix,
        stateJob,
        setStateJob,
        updateCreditInfo
      );
      isSubscribed.current = true;

      // Cleanup function to unsubscribe from events
      return () => {
        unsubscribeFromEvents(subscription, eventPrefix);
        isSubscribed.current = false;
      };
    }
  }, [etapa, socketEventPrefix, stateJob]);

  useEffect(() => {
    if (isCompleted && !isFailed && proyectoId) {
      const handleCreateResult = async () => {
        console.log(
          `useEffect x proyectoId isCompleted cargando resultados en bd etapa=${etapa} proyectoId=${proyectoId}`
        );
        //TODO identicar si se esta enviando o no el pomptLLM en las planifiaciones
        const resultadoId = await crearResultado(
          proyectoId,
          isCompleted,
          isFailed,
          etapa,
          wsProject,
          "promptLLM"
        );
        console.log({ resultadoId });
        setStateJob((prevState) => ({
          ...prevState,
          resultadoId,
          showNotification: true,
        }));
      };
      handleCreateResult();
    }
    if (etapa === 2 && isCompleted && !isFailed) {
      getProyectosByUser(userId);
    }
  }, [proyectoId, isCompleted, isFailed, etapa, userId, wsProject]);

  useEffect(() => {
    if (isCompleted && !isFailed) {
      localStorage.setItem("jobs", JSON.stringify(jobs));
    }
  }, [jobs, isCompleted, isFailed]);

  const handleStartJob = async (
    obj: iProyectoUA | iProyectoABP,
    netapa: number,
    prefix: string
  ) => {
    socket?.connect();
    console.log(
      `handleStartJob obj=${JSON.stringify(
        obj
      )} argumento etapa=${netapa} prefix=${prefix}`
    );
    updateEtapa(netapa);
    updateUnidadEtapa(netapa);
    setStateJob((prevState) => ({
      ...prevState,
      socketEventPrefix: prefix,
      propuestaUsuario: obj,
      showNotification: false,
      isCompleted: false,
      isFailed: false,
      isProcessing: true,
    }));
    console.log(`Socket emit: ${prefix}_etapa_${netapa}`);
    socket?.emit(`${prefix}_etapa_${netapa}`, { data: obj });
    await updateCreditInfo();
  };

  const handleStartJobABPBeta = async (
    obj: iProyectoUA | iProyectoABP,
    netapa: number,
    prefix: string
  ) => {
    socket?.connect();
    console.log(
      `handleStartJob obj=${JSON.stringify(
        obj
      )} argumento etapa=${netapa} prefix=${prefix}`
    );
    updateEtapaABPBeta(netapa);
    updateUnidadEtapaABPBeta(netapa);
    setStateJob((prevState) => ({
      ...prevState,
      socketEventPrefix: prefix,
      propuestaUsuario: obj,
      showNotification: false,
      isCompleted: false,
      isFailed: false,
      isProcessing: true,
    }));
    console.log(`PROBANDO BETA Socket emit: ${prefix}_etapa_${netapa}`);
    socket?.emit(`${prefix}_internacional_etapa_${netapa}`, { data: obj });
    await updateCreditInfo();
  };

  const updateEtapa = (n: number) => {
    setStateJob((prevState) => ({ ...prevState, etapa: n }));
    const data: any = msgLoadingData.find((msg) => msg.etapa === n);
    setStateJob((prevState) => ({ ...prevState, msgLoading: data.msg }));
    navigate("/asistente/process");
  };

  const updateEtapaABPBeta = (n: number) => {
    setStateJob((prevState) => ({ ...prevState, etapa: n }));
    const data: any = msgLoadingData.find((msg) => msg.etapa === n);
    setStateJob((prevState) => ({ ...prevState, msgLoading: data.msg }));
    navigate("/asistente/processabpbeta");
  };

  const updateUnidadEtapa = (n: number) => {
    setStateJob((prevState) => ({ ...prevState, etapa: n }));
    const data: any = msgLoadingUnidadData.find((msg) => msg.etapa === n);
    setStateJob((prevState) => ({ ...prevState, msgLoadingUnidad: data.msg }));
    navigate("/asistente/process");
  };

  const updateUnidadEtapaABPBeta = (n: number) => {
    setStateJob((prevState) => ({ ...prevState, etapa: n }));
    const data: any = msgLoadingUnidadData.find((msg) => msg.etapa === n);
    setStateJob((prevState) => ({ ...prevState, msgLoadingUnidad: data.msg }));
    navigate("/asistente/processabpbeta");
  };

  const handleProyectRecovery = (
    proyecto: any,
    etapa: number,
    tipoEstructura: TipoEstructura
  ) => {
    setStateJob((prevState) => ({
      ...prevState,
      wsProject: proyecto,
      etapa: etapa,
      tipoEstructura,
      jobs: { ...prevState.jobs, tipoEstructura, status: "Completado" },
      isCompleted: true,
      isFailed: false,
      isProcessing: false,
    }));
  };

  const handleSetTipoEstructuraProyecto = (tipoEstructura: TipoEstructura) => {
    setStateJob((prevState) => ({
      ...prevState,
      tipoEstructura,
      jobs: { ...prevState.jobs, tipoEstructura },
    }));
  };

  // Función para resetear el estado
  const resetState = () => {
    setStateJob((prevState) => ({
      ...initialState,
      etapa: 0,
      tipoEstructura: prevState.tipoEstructura,
      userId: prevState.userId, // Preserva el userId al resetear
      idOrganizacion: prevState.idOrganizacion,
      misProyectos: prevState.misProyectos,
      isMisProyectosLoading: prevState.isMisProyectosLoading,
      jobs: prevState.jobs,
    }));
  };

  const value = {
    handleStartJob,
    handleStartJobABPBeta,
    ...stateJob,
    //handleSetEtapa: updateEtapa,
    handleProyectRecovery,
    handleSetTipoEstructuraProyecto,
    resetState,
  };

  return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
};

const useJobContext = () => useContext(JobContext);

export { JobProvider, useJobContext };
